/* =========================================================
   G36 · GALERÍA DE PRODUCTO (estilo Nike)
   - Computador: pasar el mouse (o clic) por una miniatura cambia la foto;
     flechas anterior/siguiente; teclado ← →.
   - Celular: carrusel que se desliza con el dedo (scroll-snap) + puntos.
   - Clic en una foto: pantalla completa.
   - Al cambiar de variante (talla/color) se actualiza con el HTML que
     Horizon ya trae en el evento productSelect. Al cambiar de color se
     mantiene la posición de la foto (foto 3 negra → foto 3 blanca) y la
     foto actual se queda hasta que la nueva esté lista (sin parpadeo).
   Markup: snippets/g36-product-gallery.liquid
========================================================= */

import { StandardEvents } from '@shopify/events';

const desktop = window.matchMedia('(min-width: 750px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const ICON_CLOSE =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
const ICON_PREV =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
const ICON_NEXT =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';

class G36ProductGallery extends HTMLElement {
  index = 0;
  /** @type {AbortController | null} */
  #ac = null;
  /** @type {ResizeObserver | null} */
  #ro = null;
  #raf = 0;
  /** @type {HTMLDialogElement | null} */
  #lb = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('pointerover', this.#onPointerOver, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    // scroll no burbujea: se escucha en fase de captura
    this.addEventListener('scroll', this.#onScroll, { signal, capture: true, passive: true });

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // Cambio de color: la galería nueva llega con la foto en la que iba (foto 3 negra → foto 3 blanca)
    const start = Number(this.dataset.startIndex);
    if (start > 0 && this.count > 0) {
      this.index = Math.min(start, this.count - 1);
      delete this.dataset.startIndex;
      // Ya, sin esperar un cuadro (así no se ve un instante la foto 1); el rAF por si aún no hay layout
      this.select(this.index);
      requestAnimationFrame(() => this.select(this.index));
    }

    if (this.track) {
      this.#ro = new ResizeObserver(() => this.#jump(this.index));
      this.#ro.observe(this.track);
    }

    this.#updateThumbsFade();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ro?.disconnect();
    cancelAnimationFrame(this.#raf);
  }

  /* ---------- Referencias (se leen siempre del DOM: sobrevive a morph) ---------- */

  get track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-g36-track]'));
  }

  get slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-g36-slide]')]);
  }

  get thumbs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-g36-thumb]')]);
  }

  get count() {
    return this.slides.length;
  }

  /* ---------- Navegación ---------- */

  /**
   * @param {number} i
   * @param {{ smooth?: boolean }} [opts]
   */
  select(i, { smooth = false } = {}) {
    const n = this.count;
    if (!n || !this.track) return;
    const next = ((i % n) + n) % n;
    const behavior = smooth && !reducedMotion.matches ? 'smooth' : 'instant';
    this.#eager(next);
    this.track.scrollTo({ left: next * this.track.clientWidth, behavior: /** @type {ScrollBehavior} */ (behavior) });
    this.#setIndex(next);
  }

  /** @param {number} i */
  #jump(i) {
    if (!this.track) return;
    this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'instant' });
  }

  /** @param {number} i */
  #setIndex(i) {
    const changed = i !== this.index;
    this.index = i;

    this.thumbs.forEach((t, k) => {
      if (k === i) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
    this.querySelectorAll('.g36-pg__dot').forEach((d, k) => d.classList.toggle('is-active', k === i));

    const status = this.querySelector('[data-g36-status]');
    if (status && changed) status.textContent = `Imagen ${i + 1} de ${this.count}`;

    this.#keepThumbVisible(i);
    if (changed) this.#pauseOthers(i);
  }

  /** Carga ya la foto i (evita parpadeo al pasar el mouse) */
  /** @param {number} i */
  #eager(i) {
    this.slides[i]?.querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
  }

  /** @param {number} i */
  #keepThumbVisible(i) {
    const list = /** @type {HTMLElement | null} */ (this.querySelector('[data-g36-thumbs]'));
    const thumb = this.thumbs[i];
    if (!list || !thumb || list.scrollHeight <= list.clientHeight) return;
    const top = thumb.offsetTop - list.offsetTop;
    const bottom = top + thumb.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 28;
  }

  /** @param {number} i */
  #pauseOthers(i) {
    this.slides.forEach((slide, k) => {
      if (k === i) return;
      slide.querySelectorAll('deferred-media, product-model').forEach((m) => {
        // @ts-ignore - DeferredMedia de Horizon (assets/media.js)
        if (typeof m.pauseMedia === 'function') m.pauseMedia();
      });
      slide.querySelectorAll('video').forEach((v) => v.pause());
    });
  }

  #updateThumbsFade() {
    const list = this.querySelector('[data-g36-thumbs]');
    if (!list) return;
    const atEnd = list.scrollTop + list.clientHeight >= list.scrollHeight - 2;
    list.toggleAttribute('data-at-end', atEnd);
  }

  /* ---------- Eventos ---------- */

  /** @param {Event} event */
  #onScroll = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.matches?.('[data-g36-thumbs]')) {
      this.#updateThumbsFade();
      return;
    }
    if (target !== this.track) return;
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => {
      const track = this.track;
      if (!track || !track.clientWidth) return;
      const i = Math.round(track.scrollLeft / track.clientWidth);
      if (i !== this.index) this.#setIndex(Math.max(0, Math.min(i, this.count - 1)));
    });
  };

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (event.pointerType !== 'mouse' || !desktop.matches) return;
    const thumb = /** @type {HTMLElement | null} */ (/** @type {Element} */ (event.target).closest('[data-g36-thumb]'));
    if (!thumb) return;
    const i = Number(thumb.dataset.g36Thumb);
    if (i !== this.index) this.select(i);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const el = /** @type {Element} */ (event.target);

    const thumb = /** @type {HTMLElement | null} */ (el.closest('[data-g36-thumb]'));
    if (thumb) {
      this.select(Number(thumb.dataset.g36Thumb));
      return;
    }

    const step = /** @type {HTMLElement | null} */ (el.closest('[data-g36-step]'));
    if (step) {
      this.select(this.index + Number(step.dataset.g36Step), { smooth: true });
      return;
    }

    const zoom = /** @type {HTMLElement | null} */ (el.closest('[data-g36-zoom]'));
    if (zoom) {
      this.#openLightbox(Number(zoom.dataset.g36Zoom));
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const el = /** @type {Element} */ (event.target);
    if (!el.closest('[data-g36-track], [data-g36-thumbs], .g36-pg__arrows')) return;
    event.preventDefault();
    this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1), { smooth: true });
  };

  /** Último cambio de color pendiente (si llega otro, el anterior no se aplica) */
  /** @type {object | null} */
  #swap = null;

  /**
   * Variante nueva: Horizon trae el HTML de la sección en event.promise.
   * Si cambian las fotos (o su orden) se reemplaza la galería; si no, solo el precio de celular.
   * @param {Event} event
   */
  #onProductSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('product-card')) return;
    // @ts-ignore - ProductSelectEvent
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise
      .then((/** @type {any} */ result) => {
        const html = result?.detail?.html;
        if (!html || !this.isConnected) return;

        const next = html.querySelector(`g36-product-gallery[data-block-id="${this.dataset.blockId}"]`);
        if (!next) return;

        if (next.dataset.mediaIds !== this.dataset.mediaIds || next.dataset.productId !== this.dataset.productId) {
          const fresh = /** @type {HTMLElement} */ (document.importNode(next, true));
          // Mismo producto, otro color: se queda en la misma posición de foto
          const start = next.dataset.productId === this.dataset.productId ? this.index : 0;
          if (start > 0) fresh.dataset.startIndex = String(start);

          // Se deja la foto actual hasta que la nueva esté lista (máx. 450 ms): sin parpadeo en blanco
          const slides = fresh.querySelectorAll('[data-g36-slide]');
          const img = (slides[start] ?? slides[0])?.querySelector('img');
          let ready = Promise.resolve();
          if (img) {
            img.setAttribute('loading', 'eager');
            ready = img.decode().catch(() => {});
          }
          const token = {};
          this.#swap = token;
          Promise.race([ready, new Promise((resolve) => setTimeout(resolve, 450))]).then(() => {
            if (this.#swap === token && this.isConnected) this.replaceWith(fresh);
          });
          return;
        }

        const head = this.querySelector('[data-g36-mhead]');
        const nextHead = next.querySelector('[data-g36-mhead]');
        if (head && nextHead && head.innerHTML !== nextHead.innerHTML) head.innerHTML = nextHead.innerHTML;
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[g36-product-gallery]', error);
      });
  };

  /* ---------- Pantalla completa ---------- */

  #buildLightbox() {
    const zooms = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-g36-zoom]')]);
    const dialog = document.createElement('dialog');
    dialog.className = 'g36-pg-lb';
    dialog.setAttribute('aria-label', 'Fotos en pantalla completa');

    const slides = zooms
      .map(
        (z) =>
          `<div class="g36-pg-lb__slide" data-slide="${z.dataset.g36Zoom}"><img src="${z.dataset.zoomSrc}" alt="${
            z.dataset.zoomAlt || ''
          }" loading="lazy" decoding="async"></div>`
      )
      .join('');

    dialog.innerHTML = `
      <button type="button" class="g36-pg-lb__close" data-lb-close aria-label="Cerrar">${ICON_CLOSE}</button>
      <div class="g36-pg-lb__track" data-lb-track tabindex="-1">${slides}</div>
      ${
        zooms.length > 1
          ? `<button type="button" class="g36-pg-lb__arrow g36-pg-lb__arrow--prev" data-lb-step="-1" aria-label="Imagen anterior">${ICON_PREV}</button>
             <button type="button" class="g36-pg-lb__arrow g36-pg-lb__arrow--next" data-lb-step="1" aria-label="Imagen siguiente">${ICON_NEXT}</button>
             <p class="g36-pg-lb__count" data-lb-count aria-live="polite"></p>`
          : ''
      }`;

    const track = /** @type {HTMLElement} */ (dialog.querySelector('[data-lb-track]'));
    const count = dialog.querySelector('[data-lb-count]');
    const total = zooms.length;
    let pos = 0;
    const current = () => pos;
    const go = (/** @type {number} */ k, smooth = true) => {
      const n = ((k % total) + total) % total;
      pos = n;
      track.scrollTo({ left: n * track.clientWidth, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'instant' });
      if (count) count.textContent = `${n + 1} / ${total}`;
    };

    track.addEventListener(
      'scroll',
      () => {
        if (!dialog.open || !track.clientWidth) return;
        pos = Math.round(track.scrollLeft / track.clientWidth);
        if (count) count.textContent = `${pos + 1} / ${total}`;
      },
      { passive: true }
    );

    dialog.addEventListener('click', (e) => {
      const el = /** @type {Element} */ (e.target);
      if (el.closest('[data-lb-close]')) return dialog.close();
      const step = /** @type {HTMLElement | null} */ (el.closest('[data-lb-step]'));
      if (step) return go(current() + Number(step.dataset.lbStep));
      // Clic fuera de la foto cierra
      if (el.classList.contains('g36-pg-lb__slide')) dialog.close();
    });

    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') go(current() + 1);
      if (e.key === 'ArrowLeft') go(current() - 1);
    });

    dialog.addEventListener('close', () => {
      document.documentElement.style.removeProperty('overflow');
      const slide = /** @type {HTMLElement | undefined} */ (track.children[current()]);
      const i = Number(slide?.dataset.slide);
      if (!Number.isNaN(i)) this.select(i);
    });

    // @ts-ignore
    dialog._go = go;
    this.append(dialog);
    return dialog;
  }

  /** @param {number} slideIndex */
  #openLightbox(slideIndex) {
    if (!this.#lb || !this.#lb.isConnected) this.#lb = this.#buildLightbox();
    const dialog = this.#lb;
    const track = /** @type {HTMLElement} */ (dialog.querySelector('[data-lb-track]'));
    const k = [...track.children].findIndex((s) => /** @type {HTMLElement} */ (s).dataset.slide === String(slideIndex));

    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
    // @ts-ignore
    dialog._go(Math.max(0, k), false);
    track.focus({ preventScroll: true });
  }
}

if (!customElements.get('g36-product-gallery')) {
  customElements.define('g36-product-gallery', G36ProductGallery);
}
